import { useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import {
  STMIDBEquipmentService,
  mapSTMIDBToWLLEquipment,
  type STMIDBEquipmentUpdate,
} from '@/services/api/stmidbEquipmentService'

interface Equipment {
  id: string
  company_id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  next_inspection_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
  created_at: string
}

interface UpdateEquipmentData {
  name?: string
  part_number?: string | null
  serial_number?: string | null
  owner_id?: string | null
  description?: string | null
  status?: string
  inspection_status?: string | null
  category_id?: string | null
  next_inspection_date?: string | null
}

export const useEquipmentUpdate = () => {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateEquipment = async (
    equipmentId: string,
    updates: UpdateEquipmentData,
    options?: { source?: 'stmidb' }
  ): Promise<Equipment | null> => {
    setUpdating(true)
    setError(null)

    try {
      if (options?.source === 'stmidb') {
        // STMIDB has no separate name column: the WLL name is its description,
        // and Owner ID is its equipment_code.
        const { name, owner_id, ...rest } = updates
        const stmidbUpdates: STMIDBEquipmentUpdate = {
          ...(rest.serial_number !== undefined && { serial_number: rest.serial_number }),
          ...(rest.part_number !== undefined && { part_number: rest.part_number }),
          ...(name !== undefined && { description: name }),
          ...(rest.description !== undefined && { description: rest.description }),
          ...(owner_id !== undefined && { equipment_code: owner_id ?? '' }),
          ...(rest.inspection_status !== undefined && { status: rest.inspection_status }),
          ...(rest.next_inspection_date !== undefined && { next_inspection_date: rest.next_inspection_date }),
        }
        const row = await STMIDBEquipmentService.updateEquipment(equipmentId, stmidbUpdates)
        // category_id is resolved separately from categories_v2; omit it so
        // callers merging this result keep their resolved value.
        const { category_id: _categoryId, ...mapped } = mapSTMIDBToWLLEquipment(row)
        return mapped as unknown as Equipment
      }

      const { data, error: updateError } = await supabase
        .from('equipment_v2')
        .update(updates)
        .eq('id', equipmentId)
        .select()
        .single()

      if (updateError) throw updateError

      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update equipment'
      setError(errorMessage)
      console.error('Update error:', err)
      return null
    } finally {
      setUpdating(false)
    }
  }

  const checkSerialNumberExists = async (
    serialNumber: string,
    excludeEquipmentId?: string
  ): Promise<boolean> => {
    try {
      let query = supabase
        .from('equipment_v2')
        .select('id')
        .eq('serial_number', serialNumber)

      if (excludeEquipmentId) {
        query = query.neq('id', excludeEquipmentId)
      }

      const { data } = await query

      return (data?.length || 0) > 0
    } catch (err) {
      console.error('Error checking serial number:', err)
      return false
    }
  }

  return {
    updateEquipment,
    checkSerialNumberExists,
    updating,
    error,
  }
}

